import { action, observable } from "mobx";
import { RequestStatus } from "mobx-store";
import Connections from "../../connections";
import {
  SportDTO,
  SportToSave,
  TournamentDTO,
  TournamentToSave,
  GenderDto,
  TeamToSAVE,
  TeamDTO,
  MatchDTO,
  MatchToSave,
  EventDTO,
  EventToSave,
  CreateEventPayload,
} from "screens/liveScoring/DTO";
import SnackBarComponent from "components/SnackBarComponent/SnackBarComponent";
import { translate } from "helperFunctions/translation";
import {
  FieldFile,
  isRemoteFile,
} from "components/GeneralFormView/FormField/types";
import { uploadSingleFile } from "helperFunctions/uploadService";

export class LiveScoreStore {
  // ---------------- SPORTS ----------------
  @observable sports: SportDTO[] = [];
  @observable sport: SportDTO | {} = {};

  @observable sportsState: RequestStatus = RequestStatus.IDLE;
  @observable addEditsportState: RequestStatus = RequestStatus.IDLE;

  @action
  public async getSports(): Promise<void> {
    try {
      this.sportsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/sports`
      );
      this.sports = response.data;
      this.sportsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.sportsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async getSport(id: number): Promise<void> {
    try {
      this.sportsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/sports/${id}`
      );
      this.sport = response.data;
      this.sportsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.sportsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async createSport(sport: SportToSave): Promise<SportDTO> {
    try {
      this.addEditsportState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpPost(
        `live-scoring/sports`,
        sport
      );
      this.addEditsportState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditsportState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async updateSport(
    id: number,
    { name, nameShort }: SportToSave
  ): Promise<SportDTO> {
    try {
      this.addEditsportState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpPut(
        `live-scoring/sports/${id}`,
        { name, nameShort }
      );
      this.addEditsportState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditsportState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // ---------------- TOURNAMENTS ----------------
  @observable tournaments: TournamentDTO[] = [];
  @observable tournament: TournamentDTO | {} = {};

  @observable tournamentsState: RequestStatus = RequestStatus.IDLE;
  @observable addEditTournamentState: RequestStatus = RequestStatus.IDLE;

  @action
  public async getTournaments(): Promise<void> {
    try {
      this.tournamentsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/tournaments`
      );
      this.tournaments = response.data;
      this.tournamentsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.tournamentsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async getTournament(id: number): Promise<void> {
    try {
      this.tournamentsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/tournaments/${id}`
      );
      this.tournament = response.data;
      this.tournamentsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.tournamentsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async createTournament(
    tournament: TournamentToSave
  ): Promise<TournamentDTO> {
    try {
      this.addEditTournamentState = RequestStatus.LOADING;
      if (
        tournament.logo &&
        typeof tournament.logo !== "string" &&
        !isRemoteFile(tournament.logo)
      ) {
        tournament.logo = await uploadSingleFile(
          tournament.logo as any as File
        );
      }
      const response = await Connections.wadiDeglaAxios.httpPost(
        `live-scoring/tournaments`,
        tournament
      );
      this.addEditTournamentState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditTournamentState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async updateTournament(
    id: number,
    tournament: TournamentToSave
  ): Promise<TournamentDTO> {
    try {
      this.addEditTournamentState = RequestStatus.LOADING;
      if (
        tournament.logo &&
        typeof tournament.logo !== "string" &&
        !isRemoteFile(tournament.logo)
      ) {
        tournament.logo = await uploadSingleFile(
          tournament.logo as any as File
        );
      }
      delete tournament.id;
      const response = await Connections.wadiDeglaAxios.httpPut(
        `live-scoring/tournaments/${id}`,
        tournament
      );
      this.addEditTournamentState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditTournamentState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // Gender

  @observable genders: GenderDto[] = [];
  @observable gender: GenderDto | {} = {};

  @observable gendersState: RequestStatus = RequestStatus.IDLE;
  @observable addEditGenderState: RequestStatus = RequestStatus.IDLE;

  @action
  public async getGenders(): Promise<void> {
    try {
      this.gendersState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/genders`
      );
      this.genders = response.data;
      this.gendersState = RequestStatus.SUCCESS;
    } catch (error) {
      this.gendersState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // ---------------- TEAMS ----------------
  @observable teams: TeamDTO[] = [];
  @observable team: TeamDTO | {} = {};

  @observable teamsState: RequestStatus = RequestStatus.IDLE;
  @observable addEditTeamState: RequestStatus = RequestStatus.IDLE;

  @action
  public async getTeams(): Promise<void> {
    try {
      this.teamsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/teams`
      );
      this.teams = response.data;
      this.teamsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.teamsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async getTeam(id: number): Promise<void> {
    try {
      this.teamsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/teams/${id}`
      );
      this.team = response.data;
      this.teamsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.teamsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async createTeam(team: TeamToSAVE): Promise<TeamDTO> {
    try {
      this.addEditTeamState = RequestStatus.LOADING;
      if (
        team.logo &&
        typeof team.logo !== "string" &&
        !isRemoteFile(team.logo)
      ) {
        team.logo = await uploadSingleFile(team.logo as any as File);
      }
      const response = await Connections.wadiDeglaAxios.httpPost(
        `live-scoring/teams`,
        team
      );
      this.addEditTeamState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditTeamState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async updateTeam(id: number, team: TeamToSAVE): Promise<TeamDTO> {
    try {
      this.addEditTeamState = RequestStatus.LOADING;
      if (
        team.logo &&
        typeof team.logo !== "string" &&
        !isRemoteFile(team.logo)
      ) {
        team.logo = await uploadSingleFile(team.logo as any as File);
      }
      const response = await Connections.wadiDeglaAxios.httpPut(
        `live-scoring/teams/${id}`,
        team
      );
      this.addEditTeamState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditTeamState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // ---------------- MATCHES ----------------
  @observable matches: MatchDTO[] = [];
  @observable match: MatchDTO;

  @observable matchesState: RequestStatus = RequestStatus.IDLE;
  @observable addEditMatchState: RequestStatus = RequestStatus.IDLE;

  @action
  public async getMatches(tournamentId?: number): Promise<void> {
    try {
      this.matchesState = RequestStatus.LOADING;

      const queryString = tournamentId ? `?tournamentId=${tournamentId}` : "";

      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/matches${queryString}`
      );

      this.matches = response.data;
      this.matchesState = RequestStatus.SUCCESS;
    } catch (error) {
      this.matchesState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async getMatch(id: number): Promise<void> {
    try {
      this.matchesState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/matches/${id}`
      );
      this.match = response.data;
      this.matchesState = RequestStatus.SUCCESS;
    } catch (error) {
      this.matchesState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async createMatch(match: MatchToSave): Promise<MatchDTO> {
    try {
      this.addEditMatchState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpPost(
        `live-scoring/matches`,
        match
      );
      this.addEditMatchState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditMatchState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async updateMatch(id: number, match: MatchToSave): Promise<MatchDTO> {
    try {
      this.addEditMatchState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpPut(
        `live-scoring/matches/${id}`,
        match
      );
      this.addEditMatchState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditMatchState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // ---------------- EVENTS ----------------
  @observable events: EventDTO[] = [];
  @observable event: EventDTO | {} = {};

  @observable eventsState: RequestStatus = RequestStatus.IDLE;
  @observable addEditEventState: RequestStatus = RequestStatus.IDLE;

  @action
  public async getEventsByMatch(matchId: number): Promise<void> {
    try {
      this.eventsState = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        `live-scoring/events?matchId=${matchId}`
      );
      this.events = response.data;
      this.eventsState = RequestStatus.SUCCESS;
    } catch (error) {
      this.eventsState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async createEvent(payload: CreateEventPayload): Promise<EventDTO> {
    try {
      this.addEditEventState = RequestStatus.LOADING;

      const { event, notification } = payload;

      // Clean up event object
      const eventToSend = { ...event };
      delete eventToSend.id;
      if (!eventToSend.teamId) delete eventToSend.teamId;

      // Send request to backend
      const response = await Connections.wadiDeglaAxios.httpPost(
        `live-scoring/events`,
        {
          event: eventToSend,
          notification, // send notification only if exists
        }
      );

      this.addEditEventState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditEventState = RequestStatus.FAILED;
      console.error(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async updateEvent(id: number, event: EventToSave): Promise<EventDTO> {
    try {
      this.addEditEventState = RequestStatus.LOADING;
      delete event.id;
      if (!event.teamId) delete event.teamId;

      const response = await Connections.wadiDeglaAxios.httpPut(
        `live-scoring/events/${id}`,
        event
      );
      this.addEditEventState = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      this.addEditEventState = RequestStatus.FAILED;
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // ---------------- DELETE ACTIONS ----------------
  @action
  public async deleteSport(id: number): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpDelete(`live-scoring/sports/${id}`);
      this.sports = this.sports.filter((s) => s.id !== id);
      SnackBarComponent.getInstance().openSnackbar(
        translate("sportDeletedSuccessfully"),
        4000,
        "success"
      );
    } catch (error) {
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async deleteTournament(id: number): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpDelete(
        `live-scoring/tournaments/${id}`
      );
      this.tournaments = this.tournaments.filter((t) => t.id !== id);
      SnackBarComponent.getInstance().openSnackbar(
        translate("tournamentDeletedSuccessfully"),
        4000,
        "success"
      );
    } catch (error) {
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async deleteGender(id: number): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpDelete(`live-scoring/genders/${id}`);
      this.genders = this.genders.filter((g) => g.id !== id);
      SnackBarComponent.getInstance().openSnackbar(
        translate("genderDeletedSuccessfully"),
        4000,
        "success"
      );
    } catch (error) {
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async deleteTeam(id: number): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpDelete(`live-scoring/teams/${id}`);
      this.teams = this.teams.filter((t) => t.id !== id);
      SnackBarComponent.getInstance().openSnackbar(
        translate("teamDeletedSuccessfully"),
        4000,
        "success"
      );
    } catch (error) {
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async deleteMatch(id: number): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpDelete(`live-scoring/matches/${id}`);
      this.matches = this.matches.filter((m) => m.id !== id);
      SnackBarComponent.getInstance().openSnackbar(
        translate("matchDeletedSuccessfully"),
        4000,
        "success"
      );
    } catch (error) {
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  @action
  public async deleteEvent(id: number): Promise<void> {
    try {
      await Connections.wadiDeglaAxios.httpDelete(`live-scoring/events/${id}`);
      this.events = this.events.filter((e) => e.id !== id);
      SnackBarComponent.getInstance().openSnackbar(
        translate("eventDeletedSuccessfully"),
        4000,
        "success"
      );
    } catch (error) {
      console.log(error);
      this.handleError(error);
      return Promise.reject(error);
    }
  }

  // ---------------- COMMON ----------------
  private handleError(err: any) {
    SnackBarComponent.getInstance().openSnackbar(
      (err as any).response?.data?.message || translate("errorMessage"),
      8000,
      "error"
    );
  }
}
